/* ===========================================================
   TRM - Live Cupping | shared client bits

   No framework. The markup these files produce is the markup in
   the handover's Screens.html, element for element and class
   for class, because those screens are the specification and
   the stylesheets are already built against them.
   =========================================================== */

import type { ServerMessage } from '../shared/types.js';

/* -- the wordmark -----------------------------------------
   From the TRM brand files. Inherits its colour, so it is pale
   on the projector's ink ground and ink on a phone's bone one -
   both approved pairings. Never recoloured, never cropped. */

export const LOGO =
  '<svg viewBox="277 392.47 526 295.06" aria-hidden="true">' +
  '<path fill-rule="nonzero" d="M 496.570312 441.738281 L 512.289062 441.738281 L 512.289062 501.023438 L 496.570312 501.214844 L 485.453125 491.566406 C 479.335938 460.261719 496.570312 441.738281 496.570312 441.738281 M 467.832031 392.496094 L 277 392.496094 L 277 464.949219 L 308.503906 464.949219 L 308.503906 687.53125 L 376.765625 687.53125 L 376.765625 464.949219 L 425.921875 464.949219 C 425.683594 491.296875 429.390625 516.261719 436.378906 522.996094 C 436.539062 523.148438 436.707031 523.292969 436.871094 523.445312 L 485.691406 549.609375 L 428.320312 563.386719 C 428.316406 563.386719 428.3125 563.386719 428.3125 563.386719 L 428.308594 687.53125 L 496.570312 687.53125 L 496.570312 555.152344 L 512.320312 563.386719 L 512.320312 687.53125 L 575.332031 687.53125 L 575.332031 392.53125 L 467.832031 392.53125 Z M 467.832031 392.496094 "/>' +
  '<path fill-rule="nonzero" d="M 707.679688 392.46875 L 703.273438 547.359375 L 692.792969 547.359375 L 684.875 392.496094 L 592.96875 392.496094 L 592.96875 687.496094 L 645.503906 687.523438 L 648.039062 464.921875 L 666.449219 687.523438 L 724.269531 687.53125 L 747.867188 464.890625 L 745.210938 687.523438 L 803 687.496094 L 803 392.496094 Z M 707.679688 392.46875 "/>' +
  '<path fill-rule="nonzero" d="M 412.664062 493.046875 C 410.242188 493.046875 408.265625 495.027344 408.265625 497.445312 L 408.265625 535.601562 C 408.265625 538.019531 410.242188 540 412.664062 540 C 415.082031 540 417.058594 538.019531 417.058594 535.601562 L 417.058594 497.445312 C 417.058594 495.027344 415.082031 493.046875 412.664062 493.046875 "/>' +
  '</svg>';

export function paintLogos(root: ParentNode = document): void {
  for (const el of root.querySelectorAll('[data-logo]')) el.innerHTML = LOGO;
}

/* -- dom --------------------------------------------------- */

type Child = Node | string | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | null | undefined> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    // An empty class is not a class; `class=""` is noise in the markup.
    // Other empty attributes are meaningful (`data-logo`, `hidden`, `alt`).
    if (key === 'class' && value === '') continue;
    if (value === true) el.setAttribute(key, '');
    else el.setAttribute(key, String(value));
  }

  for (const child of children) {
    // '' is used as "render nothing"; appending it would leave an empty text node.
    if (child === null || child === undefined || child === false || child === '') continue;
    el.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }

  return el;
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function $<T extends Element = Element>(selector: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`missing element: ${selector}`);
  return el;
}

/* -- motion -----------------------------------------------
   Fade, and a short rise. That is the entire vocabulary.
   Guests who have asked their device to reduce motion get none
   of it. */

export const reducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Counts a number up. The only number in the product that animates. */
export function countUp(el: HTMLElement, to: number, ms = 1600): void {
  if (reducedMotion()) {
    el.textContent = String(to);
    return;
  }

  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / ms);
    // Ease out, so it settles rather than stopping.
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = String(Math.round(to * eased));
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = String(to);
  };
  requestAnimationFrame(step);
}

/* -- formatting -------------------------------------------- */

export function pad2(n: number): string {
  return String(Math.max(0, Math.trunc(n))).padStart(2, '0');
}

export function clockText(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`;
}

/* -- the socket -------------------------------------------
   A phone that sleeps and wakes must come back without the
   guest doing anything. Over two hours this happens to almost
   everyone, so reconnection is the normal path, not a fallback. */

export interface Wire {
  send(msg: unknown): void;
  readonly online: boolean;
}

export function connect(onMessage: (msg: ServerMessage) => void, onOpen: () => void, onStatus?: (online: boolean) => void): Wire {
  let socket: WebSocket | null = null;
  let online = false;
  let attempt = 0;
  let queue: unknown[] = [];
  let closed = false;
  /** Armed when a waking phone asks the server to prove the socket still reaches it. */
  let probe: ReturnType<typeof setTimeout> | null = null;

  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

  function setStatus(next: boolean): void {
    if (online === next) return;
    online = next;
    onStatus?.(next);
  }

  /* Every handler checks that it still belongs to the current socket.
     Without that, a phone waking at the wrong moment opens a second socket and
     the OLD one's close event nulls out the NEW, live one - after which every
     tap is silently queued against nothing. Over two hours on venue wi-fi that
     will happen to somebody. */
  function open(): void {
    if (closed) return;
    // Already connected, or a connection is already on its way.
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

    const ws = new WebSocket(url);
    socket = ws;

    ws.addEventListener('open', () => {
      if (socket !== ws) return;
      attempt = 0;
      setStatus(true);
      onOpen();

      const pending = queue;
      queue = [];
      for (const msg of pending) {
        try {
          ws.send(JSON.stringify(msg));
        } catch {
          // Put it back rather than losing a committed answer.
          if (queue.length < 12) queue.push(msg);
        }
      }
    });

    ws.addEventListener('message', (event) => {
      // Anything at all arriving is the proof the probe was waiting for.
      if (probe) {
        clearTimeout(probe);
        probe = null;
      }
      try {
        onMessage(JSON.parse(String(event.data)) as ServerMessage);
      } catch {
        /* ignore anything that is not ours */
      }
    });

    ws.addEventListener('close', () => {
      // A superseded socket closing must not disturb the live one.
      if (socket !== ws) return;
      socket = null;
      setStatus(false);
      if (closed) return;
      // Back off, but never so far that a guest is stranded mid-round.
      const delay = Math.min(4000, 300 * Math.pow(1.6, attempt++));
      setTimeout(open, delay);
    });

    ws.addEventListener('error', () => {
      if (socket === ws) ws.close();
    });
  }

  open();

  /* A phone back from a locked screen. Its socket often still says OPEN while
     the path underneath died minutes ago - iOS drops the connection when the
     page sleeps and the object never learns - and `open()` would trust that and
     do nothing, leaving every tap queued into a corpse. So the socket is asked
     for a sign of life and given four seconds; silence means it is replaced. */
  function wake(): void {
    attempt = 0;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      open();
      return;
    }
    const ws = socket;
    try {
      ws.send(JSON.stringify({ t: 'ping' }));
    } catch {
      /* the timer below deals with it */
    }
    if (probe) clearTimeout(probe);
    probe = setTimeout(() => {
      probe = null;
      if (socket !== ws) return;
      socket = null;
      setStatus(false);
      try {
        ws.close();
      } catch {
        /* already gone */
      }
      open();
    }, 4000);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') wake();
  });
  window.addEventListener('online', wake);

  return {
    send(msg: unknown) {
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify(msg));
          return;
        } catch {
          /* fall through and hold it */
        }
      }
      // Hold it rather than dropping it: an answer given on a stuttering
      // connection is still an answer the guest committed to.
      if (queue.length < 12) queue.push(msg);
    },
    get online() {
      return online;
    },
  };
}
